import { css } from '@emotion/react';
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

import useDashboardUsersAsync from 'store/useDashboardUsersAsync';
import useProjectsAsync from 'store/useProjectsAsync';
import { DashboardSortKey, DashboardSortOrder } from 'store/fetchDashboardRuns';

import IProject from 'types/IProject';
import IUserSummary from 'types/IUserSummary';

export const SORT_OPTIONS: { key: DashboardSortKey; label: string }[] = [
  { key: 'updated_on', label: 'Last updated' },
  { key: 'created_on', label: 'Created' },
  { key: 'name', label: 'Name' },
  { key: 'running_tasks', label: 'Running tasks' },
];

function userLabel(user: IUserSummary): string {
  const fullName = [user.given_name, user.family_name].filter(Boolean).join(' ').trim();
  return fullName || user.email || user.username;
}

interface FilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  selectedProjects: IProject[];
  onSelectedProjectsChange: (projects: IProject[]) => void;
  userId: string;
  onUserIdChange: (id: string) => void;
  hasRunningTasks: boolean;
  onHasRunningTasksChange: (value: boolean) => void;
  sort: DashboardSortKey;
  onSortChange: (key: DashboardSortKey) => void;
  order: DashboardSortOrder;
  onOrderChange: (value: DashboardSortOrder) => void;
}

export default function FilterBar(props: FilterBarProps) {
  const projectsAsync = useProjectsAsync();
  const projects = projectsAsync.status === 'SUCCESS' || projectsAsync.status === 'REFRESH' ? projectsAsync.data : [];

  const usersAsync = useDashboardUsersAsync();
  const users = usersAsync.status === 'SUCCESS' || usersAsync.status === 'REFRESH' ? usersAsync.data : [];

  return (
    <div
      css={css`
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 16px;

        > *:first-of-type {
          flex: 1 1 240px;
        }
      `}
    >
      <TextField
        id="search-dashboard-workflows"
        label="Search"
        variant="outlined"
        value={props.search}
        onChange={(e) => props.onSearchChange(e.target.value)}
      />
      <Autocomplete
        multiple
        options={projects}
        getOptionLabel={(project) => project.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={props.selectedProjects}
        onChange={(_, value) => props.onSelectedProjectsChange(value)}
        renderInput={(params) => <TextField {...params} label="Projects" />}
        css={css`
          min-width: 240px;
        `}
        size="small"
      />
      <FormControl
        css={css`
          min-width: 180px;
        `}
        size="small"
      >
        <InputLabel id="dashboard-user-filter-label">User</InputLabel>
        <Select
          labelId="dashboard-user-filter-label"
          label="User"
          value={props.userId}
          onChange={(e) => props.onUserIdChange(e.target.value)}
          MenuProps={{ slotProps: { paper: { style: { maxHeight: 320 } } } }}
        >
          <MenuItem value="">All users</MenuItem>
          {users
            .filter((user): user is IUserSummary & { id: string } => !!user.id)
            .map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {userLabel(user)}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Switch checked={props.hasRunningTasks} onChange={(e) => props.onHasRunningTasksChange(e.target.checked)} />
        }
        label="Has running tasks"
      />
      <FormControl
        css={css`
          min-width: 160px;
        `}
        size="small"
      >
        <InputLabel id="dashboard-sort-label">Sort by</InputLabel>
        <Select
          labelId="dashboard-sort-label"
          label="Sort by"
          value={props.sort}
          onChange={(e) => props.onSortChange(e.target.value as DashboardSortKey)}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tooltip title={props.order === 'desc' ? 'Descending' : 'Ascending'}>
        <IconButton onClick={() => props.onOrderChange(props.order === 'desc' ? 'asc' : 'desc')} size="small">
          {props.order === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
        </IconButton>
      </Tooltip>
    </div>
  );
}
